import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import DataTable from "../../../components/DataTable";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import {
  getCopyTradeMasterPerformance,
  getCopyTradeMasterStatus,
  getCopyTradeMasterTrades,
  getCopyTradeMySubscriptions,
  unsubscribeCopyTradeMaster,
} from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { isCopyTradeDisabled } from "../../../utils/permissionHelper";
import { useToast } from "../../../contexts/ToastContext";
import "../../css/List.css";
import "../../css/CopyTrade.css";
import "../../css/MasterPerformance.css";

const LINE_RANGES = ["1d", "1w", "1m", "1y", "all"];
const BAR_INTERVALS = ["week", "month"];
const TRADES_PER_PAGE = 5;
const CURRENT_UTC_YEAR = new Date().getUTCFullYear();

const pad2 = (value) => String(value).padStart(2, "0");

const utcParts = (ts) => {
  const date = new Date(Number(ts) * 1000);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    date: date.getUTCDate(),
    hours: date.getUTCHours(),
  };
};

const startOfUtcDayTs = (ts) => {
  const { year, month, date } = utcParts(ts);
  return Math.floor(Date.UTC(year, month, date) / 1000);
};

const startOfUtcMonthTs = (year, month) => Math.floor(Date.UTC(year, month, 1) / 1000);

const startOfUtcWeek = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  d.setUTCHours(0, 0, 0, 0);
  return d;
};

const labelHour = (ts) => `${pad2(utcParts(ts).hours)}:00`;
const labelDay = (ts) => {
  const parts = utcParts(ts);
  return `${pad2(parts.date)}/${pad2(parts.month + 1)}`;
};
const labelMonth = (ts) => {
  const parts = utcParts(ts);
  return `${pad2(parts.month + 1)}/${parts.year}`;
};
const labelDayYear = (ts) => {
  const parts = utcParts(ts);
  return `${pad2(parts.date)}/${pad2(parts.month + 1)} ${parts.year}`;
};

const formatPct = (value, digits = 2) =>
  `${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;

const formatProfit = (value) => {
  const abs = Math.abs(Number(value)).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return Number(value) < 0 ? `$-${abs}` : `$${abs}`;
};

const toSmoothPath = (points) => {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

const profitInRange = (points, from, to) =>
  points.reduce((sum, point) => (point.ts >= from && point.ts < to ? sum + point.profit : sum), 0);

const startingEquity = (equity, points, from) => {
  const laterProfit = points.reduce((sum, point) => (point.ts >= from ? sum + point.profit : sum), 0);
  const base = Number(equity) - laterProfit;
  if (Number.isFinite(base) && base > 0) return base;
  if (Number(equity) > 0) return Number(equity);
  return 0;
};

const roiPct = (profit, base) => {
  if (!base || base <= 0) return 0;
  return Math.round((profit / base) * 10000) / 100;
};

const lineBuckets = (range, nowTs = Math.floor(Date.now() / 1000), sinceTs = 0) => {
  const since = Number(sinceTs) > 0 ? Number(sinceTs) : 0;
  if (range === "1d") {
    const size = 3 * 3600;
    const to = Math.ceil(nowTs / size) * size;
    let from = to - 8 * size;
    if (since && from < since) from = Math.floor(since / size) * size;
    const count = Math.max(1, Math.ceil((to - from) / size));
    return Array.from({ length: count }, (_, i) => {
      const start = from + i * size;
      return { from: start, to: start + size, label: labelHour(start + size) };
    });
  }
  if (range === "1w") {
    const end = startOfUtcDayTs(nowTs);
    let start = end - 6 * 86400;
    if (since) start = Math.max(start, startOfUtcDayTs(since));
    const count = Math.max(1, Math.round((end - start) / 86400) + 1);
    return Array.from({ length: count }, (_, i) => {
      const from = start + i * 86400;
      return { from, to: from + 86400, label: labelDay(from) };
    });
  }
  if (range === "1m") {
    const to = startOfUtcDayTs(nowTs) + 86400;
    const size = 4 * 86400;
    let from = to - 8 * size;
    if (since && from < since) from = startOfUtcDayTs(since);
    const count = Math.max(1, Math.ceil((to - from) / size));
    return Array.from({ length: count }, (_, i) => {
      const start = from + i * size;
      return { from: start, to: Math.min(start + size, to), label: labelDay(Math.min(start + size, to) - 86400) };
    });
  }

  const { year, month } = utcParts(nowTs);
  const nowIndex = year * 12 + month;
  let fromIndex = nowIndex - 11;
  if (since) {
    const created = utcParts(since);
    const createdIndex = created.year * 12 + created.month;
    fromIndex = range === "all" ? createdIndex : Math.max(createdIndex, fromIndex);
  }
  const count = Math.max(1, nowIndex - fromIndex + 1);
  return Array.from({ length: count }, (_, i) => {
    const abs = fromIndex + i;
    const fromYear = Math.floor(abs / 12);
    const fromMonth = abs % 12;
    const from = startOfUtcMonthTs(fromYear, fromMonth);
    return {
      from,
      to: startOfUtcMonthTs(fromYear, fromMonth + 1),
      label: labelMonth(from),
    };
  });
};

const buildLineSeriesFromPoints = (points, range, equity, sinceTs = 0) => {
  const buckets = lineBuckets(range, Math.floor(Date.now() / 1000), sinceTs);
  const from = buckets[0]?.from ?? 0;
  const base = startingEquity(equity, points, from);
  let cumulative = 0;
  return buckets.map((bucket) => {
    cumulative += profitInRange(points, bucket.from, bucket.to);
    return { label: bucket.label, value: roiPct(cumulative, base) };
  });
};

const barMonthBuckets = (year, sinceTs = 0) => {
  const created = Number(sinceTs) > 0 ? utcParts(sinceTs) : null;
  if (created && year < created.year) return [];
  const fromMonth = created && year === created.year ? created.month : 0;
  const toMonth = year === CURRENT_UTC_YEAR ? new Date().getUTCMonth() : 11;
  if (toMonth < fromMonth) return [];
  return Array.from({ length: toMonth - fromMonth + 1 }, (_, i) => {
    const month = fromMonth + i;
    return {
      from: startOfUtcMonthTs(year, month),
      to: startOfUtcMonthTs(year, month + 1),
      label: `${pad2(month + 1)}/${year}`,
    };
  });
};

const barWeekBuckets = (year, sinceTs = 0) => {
  const yearStart = startOfUtcWeek(new Date(Date.UTC(year, 0, 4)));
  const yearEnd =
    year === CURRENT_UTC_YEAR
      ? startOfUtcWeek()
      : startOfUtcWeek(new Date(Date.UTC(year, 11, 31)));
  let start = yearStart;
  if (Number(sinceTs) > 0) {
    const createdWeek = startOfUtcWeek(new Date(Number(sinceTs) * 1000));
    if (createdWeek.getTime() > start.getTime()) start = createdWeek;
  }
  const startTs = Math.floor(start.getTime() / 1000);
  const endTs = Math.floor(yearEnd.getTime() / 1000);
  if (endTs < startTs) return [];
  const weeks = Math.floor((endTs - startTs) / (7 * 86400)) + 1;
  const maxBars = 16;
  const skip = Math.max(0, weeks - maxBars);
  return Array.from({ length: Math.min(weeks, maxBars) }, (_, i) => {
    const from = startTs + (skip + i) * 7 * 86400;
    return { from, to: from + 7 * 86400, label: labelDayYear(from) };
  });
};

const buildBarSeriesFromPoints = (points, year, interval, equity, sinceTs = 0) => {
  const buckets =
    interval === "week" ? barWeekBuckets(year, sinceTs) : barMonthBuckets(year, sinceTs);
  return buckets.map((bucket) => {
    const profit = profitInRange(points, bucket.from, bucket.to);
    const base = startingEquity(equity, points, bucket.from);
    return { label: bucket.label, value: roiPct(profit, base) };
  });
};

const yearsFromPoints = (points, sinceTs = 0) => {
  const createdYear =
    Number(sinceTs) > 0 ? new Date(Number(sinceTs) * 1000).getUTCFullYear() : CURRENT_UTC_YEAR;
  const years = new Set();
  for (let year = createdYear; year <= CURRENT_UTC_YEAR; year += 1) {
    years.add(year);
  }
  points.forEach((point) => {
    if (point.ts > 0) years.add(new Date(point.ts * 1000).getUTCFullYear());
  });
  return Array.from(years)
    .filter((year) => year >= createdYear && year <= CURRENT_UTC_YEAR)
    .sort((a, b) => a - b);
};

const extractChartPoints = (payload) => {
  const raw = payload?.POINTS ?? payload?.points ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => ({
      ts: Number(row?.TS ?? row?.ts ?? 0),
      profit: Number(row?.PROFIT ?? row?.profit ?? 0),
    }))
    .filter((point) => point.ts > 0 && Number.isFinite(point.profit));
};

const niceCeil = (value) => {
  const n = Math.abs(Number(value) || 0);
  if (n === 0) return 0;
  const mag = 10 ** Math.floor(Math.log10(n));
  const norm = n / mag;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * mag;
};

const axisRange = (values) => {
  const nums = values.filter((value) => Number.isFinite(value));
  let min = nums.length ? Math.min(0, ...nums) : 0;
  let max = nums.length ? Math.max(0, ...nums) : 0;
  if (min === 0 && max === 0) max = 10;
  const pad = Math.max(Math.abs(min), Math.abs(max), 1) * 0.08;
  min = min < 0 ? -niceCeil(Math.abs(min) + pad) : 0;
  max = max > 0 ? niceCeil(max + pad) : 0;
  if (max === min) max = min + 10;
  const ticks = Array.from({ length: 6 }, (_, i) => min + ((max - min) / 5) * i);
  return { min, max, ticks };
};

const formatAxisTick = (value) => {
  const abs = Math.abs(value);
  const digits = abs >= 10 || Math.abs(value - Math.round(value)) < 0.05 ? 0 : abs >= 1 ? 1 : 2;
  return `${Number(value.toFixed(digits))}%`;
};

const formatWeekRange = (fromTs) => {
  const from = new Date(fromTs * 1000);
  const to = new Date((fromTs + 6 * 86400) * 1000);
  const fmt = (d) =>
    d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  return `${fmt(from)} – ${fmt(to)}`;
};

const mapTradeFromApi = (row, index) => {
  const type = String(row?.TYPE ?? row?.type ?? "BUY").toLowerCase() === "sell" ? "sell" : "buy";
  const volume = Number(row?.VOLUME ?? row?.volume ?? 0);
  return {
    id: Number(row?.DEAL_ID ?? row?.deal_id ?? row?.POSITION_ID ?? index + 1),
    instrument: String(row?.SYMBOL ?? row?.symbol ?? "—"),
    openTime: String(row?.OPEN_TIME ?? row?.open_time ?? "—"),
    openPrice: String(row?.OPEN_PRICE ?? row?.open_price ?? "—"),
    closeTime: String(row?.CLOSE_TIME ?? row?.close_time ?? "—"),
    closePrice: String(row?.CLOSE_PRICE ?? row?.close_price ?? "—"),
    type,
    volume: Number.isFinite(volume) ? volume.toFixed(2) : "0.00",
    profit: Number(row?.PROFIT ?? row?.profit ?? 0),
  };
};

const extractTradesPayload = (response) => {
  const data = response?.decryptedData || response?.data || {};
  const payload = data?.DATA ?? data?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.TRADES)) return payload.TRADES;
  if (Array.isArray(payload?.trades)) return payload.trades;
  return [];
};

function ChartToggles({ options, value, onChange, getLabel }) {
  return (
    <div className="ctv-toggle" role="tablist">
      {options.map((option) => (
        <button
          key={String(option)}
          type="button"
          role="tab"
          aria-selected={value === option}
          className={`ctv-toggle-btn${value === option ? " is-active" : ""}`}
          onClick={() => onChange(option)}
        >
          {getLabel(option)}
        </button>
      ))}
    </div>
  );
}

function LineChart({ series }) {
  const rawId = useId();
  const gradientId = `ctv-line-${rawId.replace(/:/g, "")}`;
  const width = 760;
  const height = 280;
  const pad = { top: 16, right: 8, bottom: 36, left: 52 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const { min: yMin, max: yMax, ticks } = axisRange(series.map((item) => Number(item.value) || 0));
  const span = yMax - yMin || 1;
  const lastValue = Number(series[series.length - 1]?.value) || 0;
  const isDown = lastValue < 0;
  const stroke = isDown ? "#ef4444" : "#22c55e";
  const points = series.map((item, i) => ({
    x: pad.left + (series.length <= 1 ? innerW / 2 : (i / (series.length - 1)) * innerW),
    y: pad.top + innerH - ((Number(item.value) - yMin) / span) * innerH,
    label: item.label,
  }));
  const zeroY = pad.top + innerH - ((0 - yMin) / span) * innerH;
  const linePath = toSmoothPath(points);
  const areaPath = points.length
    ? `${linePath} L ${points[points.length - 1].x} ${zeroY} L ${points[0].x} ${zeroY} Z`
    : "";
  const labelStep = points.length > 8 ? 2 : 1;

  return (
    <svg
      className="ctv-line-svg"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Trading performance ${formatPct(lastValue)}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((tick) => {
        const y = pad.top + innerH - ((tick - yMin) / span) * innerH;
        return (
          <g key={`y-${tick}`}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={y}
              y2={y}
              className="ctv-grid-line"
            />
            <text x={pad.left - 8} y={y + 4} textAnchor="end" className="ctv-axis-text">
              {formatAxisTick(tick)}
            </text>
          </g>
        );
      })}
      {yMin < 0 && (
        <line
          x1={pad.left}
          x2={width - pad.right}
          y1={zeroY}
          y2={zeroY}
          className="ctv-zero-line"
        />
      )}
      {points.map((point, index) => (
        <text
          key={`x-${point.label}-${index}`}
          x={point.x}
          y={height - 8}
          textAnchor="middle"
          className="ctv-axis-text"
        >
          {index % labelStep === 0 || index === points.length - 1 ? point.label : ""}
        </text>
      ))}
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={linePath} className={`ctv-line-path${isDown ? " is-down" : ""}`} />
    </svg>
  );
}

function BarChart({ series }) {
  const peak = Math.max(...series.map((item) => Math.abs(Number(item.value) || 0)), 0);
  const yMax = Math.max(10, niceCeil(peak || 10));
  const ticks = Array.from({ length: 6 }, (_, i) => Math.round((yMax / 5) * (5 - i)));
  const slotPct = `${100 / Math.max(12, series.length || 1)}%`;

  return (
    <div className="ctv-bar-chart">
      <div className="ctv-bar-y" aria-hidden>
        {ticks.map((tick) => (
          <span key={tick}>{formatAxisTick(tick)}</span>
        ))}
      </div>
      <div className="ctv-bar-plot">
        <div className="ctv-bar-grid" aria-hidden>
          {ticks.map((tick) => (
            <span key={`grid-${tick}`} />
          ))}
        </div>
        <div className="ctv-bar-cols">
          {series.map((item, index) => {
            const value = Number(item.value) || 0;
            return (
              <div
                key={`${item.label}-${index}`}
                className="ctv-bar-col"
                style={{ flex: `0 0 ${slotPct}`, width: slotPct, maxWidth: slotPct }}
              >
                <div
                  className={`ctv-bar${value < 0 ? " is-down" : ""}`}
                  style={{
                    height: `${
                      value === 0 ? 0 : Math.min(100, Math.max(2, (Math.abs(value) / yMax) * 100))
                    }%`,
                  }}
                  title={`${item.label}: ${formatPct(value)}`}
                />
              </div>
            );
          })}
        </div>
        <div className="ctv-bar-x">
          {series.map((item, index) => (
            <span
              key={`x-${item.label}-${index}`}
              style={{ flex: `0 0 ${slotPct}`, width: slotPct, maxWidth: slotPct }}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function resolveMaster(paramLogin, stateMaster) {
  const fromState = stateMaster && typeof stateMaster === "object" ? stateMaster : {};
  const login = String(fromState.tradingLogin || paramLogin || "").trim();
  const name = String(fromState.name || "").trim() || (login ? `Master ${login}` : "Master Trader");
  return {
    ...fromState,
    name,
    initials: (fromState.initials || name.charAt(0) || "?").toUpperCase(),
    avatarColor: fromState.avatarColor || "#6B5B95",
    tradingLogin: login || "—",
    copiers: Number(fromState.copiers ?? 0),
    equity: Number(fromState.equity ?? 0),
    commission: Number(fromState.commission ?? 0),
  };
}

export default function MasterPerformance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useParams();
  const { success: showSuccess, error: showError } = useToast();
  const [user, setUser] = useState(null);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (user && isCopyTradeDisabled(user)) {
      navigate("/pages/dashboard", { replace: true });
    }
  }, [user, navigate]);
  const master = useMemo(
    () => resolveMaster(login, location.state?.master),
    [login, location.state]
  );

  const [lineRange, setLineRange] = useState("all");
  const [barYear, setBarYear] = useState(CURRENT_UTC_YEAR);
  const [barInterval, setBarInterval] = useState("month");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [weekStartTs, setWeekStartTs] = useState(() =>
    Math.floor(startOfUtcWeek().getTime() / 1000)
  );
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [tradesError, setTradesError] = useState("");
  const [maxDrawdownPct, setMaxDrawdownPct] = useState(null);
  const [investorsFunds, setInvestorsFunds] = useState(null);
  const [workingDays, setWorkingDays] = useState(null);
  const [masterSinceTs, setMasterSinceTs] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [chartPoints, setChartPoints] = useState([]);
  const [chartEquity, setChartEquity] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(null);
  const [isOwnMaster, setIsOwnMaster] = useState(false);
  const [showDisconnectPassword, setShowDisconnectPassword] = useState(false);

  const equityBase = chartEquity > 0 ? chartEquity : Number(master.equity) || 0;
  const barYears = useMemo(
    () => yearsFromPoints(chartPoints, masterSinceTs),
    [chartPoints, masterSinceTs]
  );
  const lineSeries = useMemo(
    () => buildLineSeriesFromPoints(chartPoints, lineRange, equityBase, masterSinceTs),
    [chartPoints, lineRange, equityBase, masterSinceTs]
  );
  const barSeries = useMemo(
    () => buildBarSeriesFromPoints(chartPoints, barYear, barInterval, equityBase, masterSinceTs),
    [chartPoints, barYear, barInterval, equityBase, masterSinceTs]
  );

  const currentWeekStartTs = Math.floor(startOfUtcWeek().getTime() / 1000);
  const isCurrentWeek = weekStartTs >= currentWeekStartTs;

  const fetchTrades = useCallback(async (cancelledRef) => {
    const loginNum = Number(master.tradingLogin);
    if (!loginNum) {
      if (!cancelledRef.current) {
        setTrades([]);
        setTradesLoading(false);
        setTradesError("");
      }
      return;
    }

    setTradesLoading(true);
    setTradesError("");
    try {
      const response = await getCopyTradeMasterTrades({
        LOGIN: loginNum,
        FROM: weekStartTs,
        TO: weekStartTs + 7 * 86400,
      });
      if (cancelledRef.current) return;
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code ?? 0);
      if (code && code !== AppConstants.responseOk && code !== 200) {
        setTrades([]);
        setTradesError(
          t("ctv_trades_load_error", {
            ns: "mobile",
            defaultValue: "Unable to load trades for this week.",
          })
        );
        return;
      }
      setTrades(extractTradesPayload(response).map(mapTradeFromApi));
    } catch (_) {
      if (cancelledRef.current) return;
      setTrades([]);
      setTradesError(
        t("ctv_trades_load_error", {
          ns: "mobile",
          defaultValue: "Unable to load trades for this week.",
        })
      );
    } finally {
      if (!cancelledRef.current) setTradesLoading(false);
    }
  }, [master.tradingLogin, weekStartTs, t]);

  const fetchPerformance = useCallback(async (cancelledRef) => {
    const loginNum = Number(master.tradingLogin);
    if (!loginNum) {
      if (!cancelledRef.current) {
        setMaxDrawdownPct(0);
        setInvestorsFunds(0);
        setWorkingDays(0);
        setMasterSinceTs(0);
        setChartPoints([]);
        setChartEquity(0);
        setStatsLoading(false);
      }
      return;
    }

    setStatsLoading(true);
    try {
      const response = await getCopyTradeMasterPerformance({ LOGIN: loginNum });
      if (cancelledRef.current) return;
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code ?? 0);
      if (code && code !== AppConstants.responseOk && code !== 200) {
        setMaxDrawdownPct(0);
        setInvestorsFunds(0);
        setWorkingDays(0);
        setMasterSinceTs(0);
        setChartPoints([]);
        setChartEquity(0);
        return;
      }
      const payload = data?.DATA ?? data?.data ?? {};
      setMaxDrawdownPct(Number(payload.MAX_DRAWDOWN_PERCENT ?? payload.maxDrawdownPercent ?? 0));
      const funds = Number(
        payload.SLAVE_PROFIT_PERCENT ??
          payload.slaveProfitPercent ??
          payload.INVESTORS_FUNDS ??
          payload.investorsFunds ??
          0
      );
      setInvestorsFunds(Number.isFinite(funds) ? funds : 0);
      const days = Number(payload.WORKING_DAYS ?? payload.workingDays ?? 0);
      setWorkingDays(Number.isFinite(days) ? Math.max(0, Math.round(days)) : 0);
      const created = Number(payload.CREATED_AT ?? payload.createdAt ?? 0);
      setMasterSinceTs(Number.isFinite(created) && created > 0 ? created : 0);
      setChartPoints(extractChartPoints(payload));
      const equity = Number(payload.EQUITY ?? payload.equity ?? 0);
      setChartEquity(Number.isFinite(equity) ? Math.max(0, equity) : 0);
    } catch (_) {
      if (cancelledRef.current) return;
      setMaxDrawdownPct(0);
      setInvestorsFunds(0);
      setWorkingDays(0);
      setMasterSinceTs(0);
      setChartPoints([]);
      setChartEquity(0);
    } finally {
      if (!cancelledRef.current) setStatsLoading(false);
    }
  }, [master.tradingLogin]);

  useEffect(() => {
    const cancelledRef = { current: false };
    fetchTrades(cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchTrades]);

  useEffect(() => {
    const cancelledRef = { current: false };
    const timer = setTimeout(() => {
      fetchPerformance(cancelledRef);
    }, 600);
    return () => {
      cancelledRef.current = true;
      clearTimeout(timer);
    };
  }, [fetchPerformance]);

  useEffect(() => {
    let cancelled = false;
    const masterLogin = String(master.tradingLogin || "").trim();

    const loadRelation = async () => {
      if (!masterLogin) {
        setIsSubscribed(false);
        setIsOwnMaster(false);
        return;
      }

      try {
        const [subResponse, statusResponse] = await Promise.all([
          getCopyTradeMySubscriptions(),
          getCopyTradeMasterStatus(),
        ]);
        if (cancelled) return;

        const subData = subResponse?.decryptedData || subResponse?.data || {};
        const subCode = Number(subData?.CODE ?? subData?.code ?? 0);
        if (subCode && subCode !== AppConstants.responseOk && subCode !== 200) {
          setIsSubscribed(false);
        } else {
          const payload = subData?.DATA ?? subData?.data ?? {};
          const logins = Array.isArray(payload?.MASTER_LOGINS)
            ? payload.MASTER_LOGINS
            : Array.isArray(payload?.masterLogins)
              ? payload.masterLogins
              : [];
          const items = Array.isArray(payload?.ITEMS)
            ? payload.ITEMS
            : Array.isArray(payload?.items)
              ? payload.items
              : [];
          const subscribed = new Set(
            [
              ...logins.map((value) => String(value).trim()),
              ...items.map((item) =>
                String(item?.MASTER_LOGIN ?? item?.masterLogin ?? "").trim()
              ),
            ].filter(Boolean)
          );
          setIsSubscribed(subscribed.has(masterLogin));
        }

        const statusData = statusResponse?.decryptedData || statusResponse?.data || {};
        const statusCode = Number(statusData?.CODE ?? statusData?.code ?? 0);
        if (statusCode && statusCode !== AppConstants.responseOk && statusCode !== 200) {
          setIsOwnMaster(false);
        } else {
          const payload = statusData?.DATA ?? statusData?.data ?? {};
          const status = String(payload?.STATUS ?? payload?.status ?? "none").toLowerCase();
          const ownLogin = String(
            payload?.MASTER?.LOGIN ?? payload?.master?.login ?? ""
          ).trim();
          setIsOwnMaster(status === "approved" && Boolean(ownLogin) && ownLogin === masterLogin);
        }
      } catch (_) {
        if (cancelled) return;
        setIsSubscribed(false);
        setIsOwnMaster(false);
      }
    };

    loadRelation();
    return () => {
      cancelled = true;
    };
  }, [master.tradingLogin]);

  const handleDisconnectApply = useCallback(
    async (plainPassword) => {
      const masterLogin = Number(master.tradingLogin);
      if (!masterLogin) {
        throw new Error("Missing master login");
      }
      return unsubscribeCopyTradeMaster({
        MASTER_LOGIN: masterLogin,
        PASSWORD: plainPassword,
      });
    },
    [master.tradingLogin]
  );

  const handleDisconnectPasswordClose = (result) => {
    setShowDisconnectPassword(false);

    if (result === "ok") {
      setIsSubscribed(false);
      showSuccess(
        t("copy_trade_unsubscribe_success", {
          ns: "mobile",
          defaultValue: "Disconnected from {{name}}.",
          name: master.name,
        })
      );
      return;
    }

    if (result === "canceled") {
      return;
    }

    showError(
      t("copy_trade_unsubscribe_error", {
        ns: "mobile",
        defaultValue: "Unable to disconnect. Please try again.",
      })
    );
  };

  useEffect(() => {
    if (!barYears.includes(barYear)) {
      setBarYear(barYears[barYears.length - 1] || CURRENT_UTC_YEAR);
    }
  }, [barYears, barYear]);

  useEffect(() => {
    setCurrentPage(1);
    setShowAllTrades(false);
  }, [weekStartTs, appliedSearch]);

  const filteredTrades = useMemo(() => {
    const query = appliedSearch.trim().toLowerCase();
    if (!query) return trades;
    return trades.filter((row) =>
      [row.instrument, row.type, row.openTime, row.closeTime].join(" ").toLowerCase().includes(query)
    );
  }, [trades, appliedSearch]);

  const perPage = showAllTrades ? Math.max(filteredTrades.length, 1) : TRADES_PER_PAGE;
  const totalItems = filteredTrades.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const pageTrades = filteredTrades.slice(pageStart, pageStart + perPage);
  const showingFrom = totalItems === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + perPage, totalItems);

  const stats = [
    {
      value: maxDrawdownPct == null ? "—" : formatPct(maxDrawdownPct),
      label: t("ctv_max_drawdown", { ns: "mobile", defaultValue: "MAX DRAWDOWN" }),
    },
    { value: "$0", label: t("ctv_min_investment", { ns: "mobile", defaultValue: "MIN INVESTMENT" }) },
    {
      value: investorsFunds == null ? "—" : formatPct(investorsFunds),
      label: t("ctv_investors_funds", { ns: "mobile", defaultValue: "INVESTORS' FUNDS" }),
    },
    {
      value: Number(master.copiers || 0).toLocaleString("en-US"),
      label: t("ctv_investors", { ns: "mobile", defaultValue: "INVESTORS" }),
    },
    {
      value: `${Number.isFinite(Number(master.commission)) ? Number(master.commission) : 0}%`,
      label: t("ctv_commission", { ns: "mobile", defaultValue: "COMMISSION" }),
    },
    {
      value: workingDays == null ? "—" : Number(workingDays).toLocaleString("en-US"),
      label: t("ctv_working_days", { ns: "mobile", defaultValue: "WORKING DAYS" }),
    },
  ];

  const columns = useMemo(
    () => [
      {
        key: "instrument",
        label: t("ctv_col_instrument", { ns: "mobile", defaultValue: "INSTRUMENT" }),
        className: "ctv-col-instrument",
        render: (row) => <span className="ctv-instrument">{row.instrument}</span>,
      },
      {
        key: "openTime",
        label: t("ctv_col_open_time", { ns: "mobile", defaultValue: "OPEN TIME" }),
        className: "ctv-col-time",
        render: (row) => <span className="ctv-muted ltr-content">{row.openTime}</span>,
      },
      {
        key: "openPrice",
        label: t("ctv_col_open_price", { ns: "mobile", defaultValue: "OPEN PRICE" }),
        className: "ctv-col-price",
        render: (row) => <span className="ltr-content">{row.openPrice}</span>,
      },
      {
        key: "closeTime",
        label: t("ctv_col_close_time", { ns: "mobile", defaultValue: "CLOSE TIME" }),
        className: "ctv-col-time",
        render: (row) => <span className="ctv-muted ltr-content">{row.closeTime}</span>,
      },
      {
        key: "closePrice",
        label: t("ctv_col_close_price", { ns: "mobile", defaultValue: "CLOSE PRICE" }),
        className: "ctv-col-price",
        render: (row) => <span className="ltr-content">{row.closePrice}</span>,
      },
      {
        key: "type",
        label: t("ctv_col_type", { ns: "mobile", defaultValue: "TYPE" }),
        className: "ctv-col-type",
        render: (row) => (
          <span className={`ctv-type ctv-type--${row.type}`} title={row.type}>
            <i
              className={`fa-solid ${row.type === "buy" ? "fa-arrow-up" : "fa-arrow-down"}`}
              aria-hidden
            />
          </span>
        ),
      },
      {
        key: "volume",
        label: t("ctv_col_volume", { ns: "mobile", defaultValue: "VOLUME" }),
        className: "ctv-col-volume",
        render: (row) => <span className="ltr-content">{row.volume}</span>,
      },
      {
        key: "profit",
        label: t("ctv_col_profit", { ns: "mobile", defaultValue: "PROFIT" }),
        className: "ctv-col-profit",
        render: (row) => (
          <span className={`ctv-profit ${row.profit >= 0 ? "is-up" : "is-down"} ltr-content`}>
            {formatProfit(row.profit)}
          </span>
        ),
      },
    ],
    [t]
  );

  const exportTrades = () => {
    const header = [
      "INSTRUMENT",
      "OPEN TIME",
      "OPEN PRICE",
      "CLOSE TIME",
      "CLOSE PRICE",
      "TYPE",
      "VOLUME",
      "PROFIT",
    ];
    const lines = [
      header.join(","),
      ...filteredTrades.map((row) =>
        [
          row.instrument,
          row.openTime,
          row.openPrice,
          row.closeTime,
          row.closePrice,
          row.type,
          row.volume,
          row.profit,
        ].join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `trades-${master.tradingLogin || "master"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const rangeLabel = (range) => {
    if (range === "all") {
      return t("ctv_range_all", { ns: "mobile", defaultValue: "All period" });
    }
    return range;
  };

  return (
    <div className="copy-trade-page ctv-page">
      <Sidebar user={user} />
      <div className="copy-trade-content accounts-list-content">
        <div className="copy-trade-header">
          <div className="copy-trade-header-text">
            <button
              type="button"
              className="ctv-back"
              onClick={() => navigate("/pages/copy-trade")}
            >
              <i className="fa-solid fa-chevron-left" aria-hidden />
              {t("copy_trade", { ns: "mobile", defaultValue: "Copy Trading" })}
            </button>
            <h1 className="copy-trade-title">
              {t("ctv_page_title", { ns: "mobile", defaultValue: "Strategy Performance" })}
            </h1>
          </div>
        </div>

        <div className="ctv-top-grid">
          <section className="ctv-card">
            <div className="ctv-card-header">
              <h2 className="ctv-card-title">
                {t("ctv_trading_performance", { ns: "mobile", defaultValue: "TRADING PERFORMANCE" })}
              </h2>
              <ChartToggles
                options={LINE_RANGES}
                value={lineRange}
                onChange={setLineRange}
                getLabel={rangeLabel}
              />
            </div>
            {statsLoading ? (
              <div className="ctv-chart-skeleton accounts-skeleton-bar" aria-hidden />
            ) : (
              <LineChart series={lineSeries} />
            )}
          </section>

          <aside className="ctv-card ctv-strategy-card">
            <div className="ctv-strategy-head">
              <span className="ctv-strategy-avatar" style={{ backgroundColor: master.avatarColor }}>
                {master.initials}
              </span>
              <div className="ctv-strategy-meta">
                <h2 className="ctv-strategy-name">{master.name}</h2>
              </div>
            </div>

            <div
              className="ctv-stats-grid"
              aria-busy={statsLoading}
              aria-label={
                statsLoading
                  ? t("loading", { ns: "mobile", defaultValue: "Loading..." })
                  : undefined
              }
            >
              {stats.map((stat) => (
                <div key={stat.label} className="ctv-stat">
                  {statsLoading ? (
                    <span className="accounts-skeleton-bar ctv-stat-skeleton" aria-hidden />
                  ) : (
                    <span className="ctv-stat-value ltr-content">{stat.value}</span>
                  )}
                  <span className="ctv-stat-label">{stat.label}</span>
                </div>
              ))}
            </div>

            {isOwnMaster ? (
              <button
                type="button"
                className="copy-trade-btn copy-trade-btn--primary ctv-connect-btn"
                disabled
                title={t("copy_trade_own_master", {
                  ns: "mobile",
                  defaultValue: "This is your master account",
                })}
              >
                {t("copy_trade_you", { ns: "mobile", defaultValue: "You" })}
              </button>
            ) : isSubscribed === null ? (
              <span className="accounts-skeleton-bar ctv-connect-btn" aria-hidden />
            ) : isSubscribed ? (
              <button
                type="button"
                className="copy-trade-btn copy-trade-btn--disconnect ctv-connect-btn"
                onClick={() => setShowDisconnectPassword(true)}
              >
                {t("ctv_disconnect", { ns: "mobile", defaultValue: "DISCONNECT" })}
              </button>
            ) : (
              <button
                type="button"
                className="copy-trade-btn copy-trade-btn--primary ctv-connect-btn"
                onClick={() =>
                  navigate("/pages/copy-trade", { state: { copyMaster: master } })
                }
              >
                {t("ctv_connect", { ns: "mobile", defaultValue: "CONNECT TO STRATEGY" })}
              </button>
            )}
          </aside>
        </div>

        <section className="ctv-card">
          <div className="ctv-card-header">
            <h2 className="ctv-card-title">
              {t("ctv_trading_performance", { ns: "mobile", defaultValue: "TRADING PERFORMANCE" })}
            </h2>
            <div className="ctv-card-toggles">
              <ChartToggles
                options={barYears}
                value={barYear}
                onChange={setBarYear}
                getLabel={(year) => String(year)}
              />
              <ChartToggles
                options={BAR_INTERVALS}
                value={barInterval}
                onChange={setBarInterval}
                getLabel={(interval) =>
                  interval === "week"
                    ? t("ctv_week", { ns: "mobile", defaultValue: "Week" })
                    : t("ctv_month", { ns: "mobile", defaultValue: "Month" })
                }
              />
            </div>
          </div>
          {statsLoading ? (
            <div className="ctv-chart-skeleton accounts-skeleton-bar" aria-hidden />
          ) : (
            <BarChart series={barSeries} />
          )}
        </section>

        <DataTable
          title={t("ctv_history_title", {
            ns: "mobile",
            defaultValue: "HISTORY OF PAST TRADES",
          })}
          subtitle={formatWeekRange(weekStartTs)}
          columns={columns}
          data={pageTrades}
          rowKey={(row, index) => `${row.id}-${index}`}
          currentPage={safePage}
          totalItems={totalItems}
          perPage={perPage}
          onPageChange={setCurrentPage}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onSearch={() => {
            setAppliedSearch(searchTerm);
            setCurrentPage(1);
          }}
          onClearSearch={() => {
            setSearchTerm("");
            setAppliedSearch("");
            setCurrentPage(1);
          }}
          searchPlaceholder={t("ctv_search_trades", {
            ns: "mobile",
            defaultValue: "Search trades...",
          })}
          loading={tradesLoading}
          loadingMessage={t("loading", { ns: "mobile", defaultValue: "Loading..." })}
          error={tradesError || undefined}
          noDataMessage={t("ctv_no_trades_week", {
            ns: "mobile",
            defaultValue: "No trades for this account in this week.",
          })}
          containerClassName="container-fluid px-0 w-100 copy-trade-table-container ctv-table-container"
          tableClassName="copy-trade-masters-table ctv-trades-table"
          paginationLeading={
            <span className="ib-clients-pagination-summary">
              {t("ctv_showing", {
                ns: "mobile",
                defaultValue: "Showing {{from}} to {{to}} of {{total}} trades",
                from: showingFrom,
                to: showingTo,
                total: totalItems,
              })}
            </span>
          }
          headerActions={
            <>
              <div className="ctv-week-nav">
                <button
                  type="button"
                  className="ctv-week-btn"
                  onClick={() => setWeekStartTs((ts) => ts - 7 * 86400)}
                  aria-label={t("ctv_prev_week", { ns: "mobile", defaultValue: "Previous week" })}
                >
                  <i className="fa-solid fa-chevron-left" aria-hidden />
                </button>
                <span className="ctv-week-label ltr-content">{formatWeekRange(weekStartTs)}</span>
                <button
                  type="button"
                  className="ctv-week-btn"
                  onClick={() => setWeekStartTs((ts) => ts + 7 * 86400)}
                  disabled={isCurrentWeek}
                  aria-label={t("ctv_next_week", { ns: "mobile", defaultValue: "Next week" })}
                >
                  <i className="fa-solid fa-chevron-right" aria-hidden />
                </button>
              </div>
              <button
                type="button"
                className="ctv-header-link"
                onClick={() => {
                  setShowAllTrades(true);
                  setCurrentPage(1);
                }}
              >
                {t("ctv_view_all_trades", { ns: "mobile", defaultValue: "View All Trades" })}
              </button>
              <button
                type="button"
                className="action-icon-btn ctv-export-btn"
                onClick={exportTrades}
                aria-label={t("ctv_export", { ns: "mobile", defaultValue: "Export" })}
                title={t("ctv_export", { ns: "mobile", defaultValue: "Export" })}
              >
                <i className="fa-solid fa-file-export" aria-hidden />
              </button>
            </>
          }
        />
      </div>

      <TransactionPasswordDialog
        open={showDisconnectPassword}
        onClose={handleDisconnectPasswordClose}
        applyFn={handleDisconnectApply}
        title={t("copy_trade_confirm_disconnect_title", {
          ns: "mobile",
          defaultValue: "Confirm disconnect",
        })}
        firstMessage={t("copy_trade_confirm_disconnect_message", {
          ns: "mobile",
          defaultValue:
            "You will stop copying trades from {{name}}. Confirm with your password to proceed.",
          name: master.name,
        })}
      />
    </div>
  );
}
